import { useQueries } from '@tanstack/react-query';
import { noteBasisQuery } from '../../../api/queries';
import type { InvoiceKind, NoteBasis, NoteBasisLine, NoteSource } from '../../../api/types';

/** The ETA identifier, shortened the way the list and the reference step do (never the full uuid). */
const shortEtaId = (uuid: string | null): string | null =>
  uuid ? (uuid.length > 8 ? `${uuid.slice(0, 8)}…` : uuid) : null;

/**
 * A reference the server returns only by id (a `?from=` deep link or a reopened draft), resolved
 * through `note-basis` into the same NoteSource shape the add field hands back on a pick.
 */
export function sourceFromBasis(basis: NoteBasis): NoteSource {
  const d = basis.document;
  return {
    id: d.id,
    kind: d.kind,
    number: d.number,
    receiverName: d.receiverName,
    receiverId: d.receiverId,
    receiverPartyId: d.receiverPartyId,
    receiverType: null,
    currency: d.currency,
    totalAmount: d.totalAmount,
    dateTimeIssued: d.dateTimeIssued,
    issueDate: null,
    etaId: shortEtaId(d.etaUuid),
    remaining: basis.cap ? basis.cap.remaining : null,
  };
}

/** A referenced line with the number of the invoice it came from (for the origin tag). */
export interface ReferenceLineInfo {
  invoiceNumber: string;
  line: NoteBasisLine;
}

/**
 * Block 11: resolve a note's references through `note-basis` — the line facts (for step 3 and the
 * step-4 review line table, role-4 M-2) and the NoteSource of each reference (for the step-4 buyer
 * and reference rows, robust to a reopened draft whose picked-sources state is empty, role-4 m-3).
 * The queries are shared through react-query, so step 3 and the review read each reference once.
 */
export function useNoteReferences(
  orgId: string,
  kind: InvoiceKind,
  references: readonly string[],
): { refLines: Map<string, ReferenceLineInfo>; sources: Record<string, NoteSource> } {
  const results = useQueries({
    queries: references.map((id) => ({ ...noteBasisQuery(orgId, id, kind) })),
  });
  const refLines = new Map<string, ReferenceLineInfo>();
  const sources: Record<string, NoteSource> = {};
  for (const r of results) {
    const basis = r.data;
    if (!basis) continue;
    const invoiceNumber = basis.document.number ?? basis.document.id;
    for (const line of basis.lines) refLines.set(line.id, { invoiceNumber, line });
    sources[basis.document.id] = sourceFromBasis(basis);
  }
  return { refLines, sources };
}
